import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getNotifications, type Notification } from '../services/notificationService';
import toast from 'react-hot-toast';

const POLL_INTERVAL = 5000;

interface AdminNotificationContextType {
  unreadCount: number;
  recentNotifications: Notification[];
  refreshNotifications: () => Promise<void>;
}

const AdminNotificationContext = createContext<AdminNotificationContextType>({
  unreadCount: 0,
  recentNotifications: [],
  refreshNotifications: async () => {},
});

export const useAdminNotification = () => useContext(AdminNotificationContext);

export const AdminNotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState<Notification[]>([]);
  const prevUnreadRef = useRef<number | null>(null);
  const isFirstLoadRef = useRef(true);

  const fetchAndNotify = async () => {
    try {
      const res = await getNotifications({ page: 1, limit: 5 });
      const notifications = res?.notifications ?? [];
      const unread = res?.unreadCount ?? 0;

      setUnreadCount(unread);
      setRecentNotifications(notifications);

      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        prevUnreadRef.current = unread;
      } else {
        const prevUnread = prevUnreadRef.current ?? 0;
        if (unread > prevUnread) {
          const added = unread - prevUnread;
          toast.success(
            added === 1
              ? 'Có khách hàng liên hệ mới'
              : `Có ${added} liên hệ mới cần xử lý`,
            { duration: 5000, icon: '📩' }
          );
        }
      }
      prevUnreadRef.current = unread;
    } catch {
      // ignore
    }
  };

  const refreshNotifications = async () => {
    await fetchAndNotify();
  };

  useEffect(() => {
    fetchAndNotify();
    const timer = setInterval(fetchAndNotify, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  return (
    <AdminNotificationContext.Provider value={{ unreadCount, recentNotifications, refreshNotifications }}>
      {children}
    </AdminNotificationContext.Provider>
  );
};
